import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { translationService } from "../../services/translation/TranslationService";
import { MAIN_INFO_FIELDS } from "../../services/ocr/mainInfoFields";

export interface MainInfoPair {
  key: string;
  value: string;
}

interface MainInfoEditDialogProps {
  open: boolean;
  initial: MainInfoPair[];
  onSave: (items: MainInfoPair[]) => void;
  onCancel: () => void;
}

const t = (key: string) => translationService.translate(key);

const norm = (s: string) => (s ?? "").trim().toLowerCase();
const inputType = (type: string) => (type === "date" ? "date" : type === "time" ? "time" : "text");

// Editor for the FIXED list of inspection main-information fields. Every field is
// shown (prefilled from the current values, matched by label); on save only the
// filled fields are returned. The caller batches them into the comparison save.
const MainInfoEditDialog: React.FC<MainInfoEditDialogProps> = ({
  open,
  initial,
  onSave,
  onCancel,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    // Prefill by matching existing entries to the fixed labels (case-insensitive).
    const existing = new Map(initial.map((p) => [norm(p.key), p.value]));
    const seeded: Record<string, string> = {};
    MAIN_INFO_FIELDS.forEach((f) => {
      seeded[f.label] = existing.get(norm(f.label)) ?? "";
    });
    setValues(seeded);
  }, [open, initial]);

  const set = (label: string, v: string) => setValues((prev) => ({ ...prev, [label]: v }));

  const handleSave = () =>
    onSave(
      MAIN_INFO_FIELDS
        .map((f) => ({ key: f.label, value: (values[f.label] ?? "").trim() }))
        .filter((p) => p.value !== "")
    );

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t("ocrService.mainInfoEdit.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1 }}>
          {MAIN_INFO_FIELDS.map((f) => (
            <TextField
              key={f.label}
              label={f.label}
              type={inputType(f.type)}
              value={values[f.label] ?? ""}
              onChange={(e) => set(f.label, e.target.value)}
              size="small"
              InputLabelProps={f.type === "string" ? undefined : { shrink: true }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t("general.cancel")}</Button>
        <Button variant="contained" onClick={handleSave}>
          {t("general.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MainInfoEditDialog;
